/** @format */

import React from "react";
import {
  XMarkIcon,
  BookOpenIcon,
  UserIcon,
  TagIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import { Book } from "../types";
import { formatCurrency, formatDate, generateBookAffiliateLink, generateAffiliateText } from "../utils/formatUtils";
import { useShare } from "../hooks/useShare";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

interface BookDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  book: Book | null;
}

const BookDetailsModal: React.FC<BookDetailsModalProps> = ({
  isOpen,
  onClose,
  book,
}) => {
  const { currentUser } = useAuth();
  const { handleShare } = useShare();

  if (!isOpen || !book) return null;

  // Share functionality with affiliate code
  const handleShareClick = async () => {
    const bookUrl = generateBookAffiliateLink(book.id, currentUser?.referralCode);
    const shareText = generateAffiliateText(book.title, currentUser?.referralCode);

    await handleShare({
      title: book.title,
      text: shareText,
      url: bookUrl,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-lg mx-auto sm:max-w-2xl lg:max-w-3xl">
          {/* Header with Close Button */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Book Details</h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleShareClick}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                title="Share book"
                aria-label="Share this book"
              >
                <ShareIcon className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                title="Close modal"
                aria-label="Close book details"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Book Content - Single Column Layout */}
          <div className="p-4 sm:p-6">
            {/* Book Cover - Centered with Badge */}
            <div className="flex justify-center mb-6">
              <div className="w-32 h-40 sm:w-40 sm:h-52 bg-gray-200 rounded-lg overflow-hidden shadow-lg relative">
                {book.coverImage ? (
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                    <BookOpenIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />
                  </div>
                )}
                
                {/* Price Badge */}
                <div className="absolute top-2 right-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  {book.amount === 0 ? "FREE" : formatCurrency(book.amount)}
                </div>
                
                {/* Program Badge */}
                {book.program && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                    {book.program}
                  </div>
                )}
              </div>
            </div>

            {/* Book Title and Author - Centered */}
            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
                {book.title}
              </h1>
              <p className="text-lg sm:text-xl text-gray-600">
                by <span className="font-semibold text-gray-800">{book.author}</span>
              </p>
            </div>

            {/* Rating - Enhanced with Half-Stars and Better Design */}
            <div className="mb-6">
              <div className="flex flex-col items-center justify-center space-y-3">
                {/* Star Rating Display */}
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => {
                    const starValue = i + 1;
                    const ratingDiff = (book.rating || 0) - i;
                    
                    let starClass = "text-gray-300"; // Empty star
                    let starIcon = "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z";
                    
                    if (ratingDiff >= 1) {
                      starClass = "text-yellow-400"; // Full star
                    } else if (ratingDiff > 0) {
                      starClass = "text-yellow-400"; // Half star (we'll handle this with CSS)
                    }
                    
                    return (
                      <div key={i} className="relative">
                        {/* Background star (always gray) */}
                        <svg
                          className="h-7 w-7 text-gray-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d={starIcon} />
                        </svg>
                        
                        {/* Foreground star (colored) */}
                        {ratingDiff > 0 && (
                          <svg
                            className={`h-7 w-7 absolute top-0 left-0 ${starClass}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            style={{
                              clipPath: ratingDiff >= 1 
                                ? 'inset(0 0 0 0)' 
                                : `inset(0 ${(1 - ratingDiff) * 100}% 0 0)`
                            }}
                          >
                            <path d={starIcon} />
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Rating Score and Review Count */}
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {book.rating ? book.rating.toFixed(1) : "0.0"}
                    </span>
                    <span className="text-lg text-gray-500">/ 5</span>
                  </div>
                  
                  {/* Review Count with Icon */}
                  <div className="flex items-center justify-center space-x-1 mt-1">
                    <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-500">
                      {book.reviewCount ? `${book.reviewCount} review${book.reviewCount === 1 ? '' : 's'}` : 'No reviews yet'}
                    </span>
                  </div>
                </div>
                
                {/* Rating Summary or No Rating Message */}
                {book.rating && book.rating > 0 ? (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg px-4 py-2 border border-yellow-200">
                    <p className="text-sm text-yellow-800 font-medium">
                      {book.rating >= 4.5 ? "⭐ Excellent" :
                       book.rating >= 4.0 ? "👍 Very Good" :
                       book.rating >= 3.5 ? "😊 Good" :
                       book.rating >= 3.0 ? "😐 Average" :
                       book.rating >= 2.5 ? "😕 Below Average" :
                       "😞 Poor"}
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                    <p className="text-sm text-gray-600 font-medium">
                      No ratings yet • Be the first to rate this book!
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Description - Full Width */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                Description
              </h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 leading-relaxed text-center">
                  {book.description ||
                    "No description available for this book."}
                </p>
              </div>
            </div>

            {/* Book Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TagIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Category</p>
                    <p className="font-semibold text-gray-900">{book.category}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <BookOpenIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Program</p>
                    <p className="font-semibold text-gray-900">{book.program}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <UserIcon className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Author</p>
                    <p className="font-semibold text-gray-900">{book.author}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Published</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(book.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Features */}
            <div className="pt-4 border-t border-gray-200 mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                Features & Benefits
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-sm font-medium text-green-800">PDF Format</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-sm font-medium text-blue-800">Instant Download</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-3 border border-purple-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-sm font-medium text-purple-800">Mobile Friendly</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="text-sm font-medium text-orange-800">Lifetime Access</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - Centered with Flair */}
            <div className="space-y-3 max-w-sm mx-auto">
              <button className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-4 text-center text-base font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                <ArrowDownTrayIcon className="h-5 w-5 mr-2 inline" />
                {book.amount === 0 ? "Get Free Now" : "Purchase & Download"}
              </button>

              <button className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 text-center text-base font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <EyeIcon className="h-5 w-5 mr-2 inline" />
                Preview Sample
              </button>
            </div>

            {/* Bottom Close Button - Prominent and Centered */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-center">
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  title="Close book details"
                  aria-label="Close book details modal"
                >
                  <XMarkIcon className="h-5 w-5 mr-2" />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDetailsModal;
